import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link2, Facebook, Check, Save, Loader2 } from "lucide-react";

const OTHER_NETS = [
  { key: "twitter", label: "X (Twitter)" }, { key: "linkedin", label: "LinkedIn" },
  { key: "tiktok", label: "TikTok" }, { key: "youtube", label: "YouTube" }, { key: "pinterest", label: "Pinterest" },
];
const emptyConn = { meta_app_id: "", meta_app_secret: "", meta_page_id: "", meta_page_token: "", meta_ig_business_id: "", meta_api_version: "v26.0", others: {} };

const Section = ({ icon: Icon, title, desc, children, accent = "text-fuchsia-300" }) => (
  <div className="rounded-3xl border border-white/10 bg-card/40 p-5 sm:p-6">
    <div className="mb-1 flex items-center gap-2">
      <Icon size={17} className={accent} />
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
    </div>
    {desc && <p className="mb-4 text-sm text-muted-foreground">{desc}</p>}
    {children}
  </div>
);

export function SocialConnectionsPanel({ accountId }) {
  const [conn, setConn] = useState(null);
  const [connForm, setConnForm] = useState(emptyConn);
  const [savingConn, setSavingConn] = useState(false);
  const [testingConn, setTestingConn] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const qs = accountId ? `?account_id=${accountId}` : "";

  const loadConn = async () => {
    try {
      const r = await api.get(`/admin/social-connections${qs}`);
      setConn(r.data);
      setConnForm((f) => ({ ...f, meta_app_id: r.data.meta_app_id || "", meta_page_id: r.data.meta_page_id || "", meta_ig_business_id: r.data.meta_ig_business_id || "", meta_api_version: r.data.meta_api_version || "v26.0" }));
    } catch { setConn(null); }
  };
  useEffect(() => { loadConn(); setTestResult(null); }, [accountId]);

  const saveConn = async () => {
    setSavingConn(true);
    try { const r = await api.put(`/admin/social-connections${qs}`, connForm); setConn(r.data); setConnForm((f) => ({ ...f, meta_app_secret: "", meta_page_token: "", others: {} })); toast.success("Connexions enregistrées"); }
    catch { toast.error("Erreur"); } finally { setSavingConn(false); }
  };
  const testConn = async () => {
    setTestingConn(true); setTestResult(null);
    try { const r = await api.post(`/admin/social-connections/test${qs}`); setTestResult(r.data); }
    catch { toast.error("Test impossible"); } finally { setTestingConn(false); }
  };
  const setOther = (k, v) => setConnForm((f) => ({ ...f, others: { ...f.others, [k]: v } }));

  return (
    <Section icon={Link2} title="Connexions réseaux (auto-publication)" desc="Renseignez vos accès pour publier automatiquement. Facebook & Instagram sont actifs via Meta ; les autres réseaux sont enregistrés (auto-publication bientôt disponible, publication assistée en attendant).">
      <div className="space-y-4">
        <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground"><Facebook size={15} className="text-[#4293ff]" /> Meta — Facebook &amp; Instagram</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>App ID</Label><Input value={connForm.meta_app_id} onChange={(e) => setConnForm({ ...connForm, meta_app_id: e.target.value })} data-testid="conn-app-id" /></div>
            <div><Label>App Secret {conn?.meta_app_secret_set && <span className="text-xs text-emerald-400">(enregistré)</span>}</Label><Input type="password" placeholder={conn?.meta_app_secret_set ? "•••••• (laisser vide pour garder)" : ""} value={connForm.meta_app_secret} onChange={(e) => setConnForm({ ...connForm, meta_app_secret: e.target.value })} data-testid="conn-app-secret" /></div>
            <div><Label>Page ID (Facebook)</Label><Input value={connForm.meta_page_id} onChange={(e) => setConnForm({ ...connForm, meta_page_id: e.target.value })} data-testid="conn-page-id" /></div>
            <div><Label>Page Access Token {conn?.meta_page_token_set && <span className="text-xs text-emerald-400">(enregistré)</span>}</Label><Input type="password" placeholder={conn?.meta_page_token_set ? "•••••• (laisser vide pour garder)" : ""} value={connForm.meta_page_token} onChange={(e) => setConnForm({ ...connForm, meta_page_token: e.target.value })} data-testid="conn-page-token" /></div>
            <div><Label>Instagram Business Account ID</Label><Input value={connForm.meta_ig_business_id} onChange={(e) => setConnForm({ ...connForm, meta_ig_business_id: e.target.value })} data-testid="conn-ig-id" /></div>
            <div><Label>Version API</Label><Input value={connForm.meta_api_version} onChange={(e) => setConnForm({ ...connForm, meta_api_version: e.target.value })} /></div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className={`rounded-full px-2 py-0.5 text-[11px] ${conn?.facebook_connected ? "bg-emerald-500/15 text-emerald-300" : "bg-white/10 text-muted-foreground"}`} data-testid="conn-status-fb">Facebook {conn?.facebook_connected ? "connecté" : "non connecté"}</span>
            <span className={`rounded-full px-2 py-0.5 text-[11px] ${conn?.instagram_connected ? "bg-emerald-500/15 text-emerald-300" : "bg-white/10 text-muted-foreground"}`} data-testid="conn-status-ig">Instagram {conn?.instagram_connected ? "connecté" : "non connecté"}</span>
          </div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
          <div className="mb-3 text-sm font-semibold text-foreground">Autres réseaux</div>
          <div className="grid gap-3 sm:grid-cols-2">
            {OTHER_NETS.map((n) => (
              <div key={n.key}><Label>{n.label} {conn?.others_set?.[n.key] && <span className="text-xs text-emerald-400">(enregistré)</span>}</Label><Input type="password" placeholder={conn?.others_set?.[n.key] ? "•••••• (laisser vide pour garder)" : "Jeton / clé API"} value={connForm.others[n.key] || ""} onChange={(e) => setOther(n.key, e.target.value)} data-testid={`conn-other-${n.key}`} /></div>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Auto-publication automatique bientôt disponible pour ces réseaux — publication assistée (1 clic) en attendant.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" className="rounded-full" onClick={saveConn} disabled={savingConn} data-testid="conn-save">{savingConn ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Save size={15} className="mr-1.5" />} Enregistrer les connexions</Button>
          <Button size="sm" variant="outline" className="rounded-full border-white/15" onClick={testConn} disabled={testingConn} data-testid="conn-test">{testingConn ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Check size={15} className="mr-1.5" />} Tester la connexion</Button>
        </div>
        {testResult && (
          <div className="flex flex-wrap gap-2 text-xs" data-testid="conn-test-result">
            <span className={`rounded-full px-2 py-1 ${testResult.facebook.ok ? "bg-emerald-500/15 text-emerald-300" : "bg-destructive/15 text-destructive"}`}>Facebook : {testResult.facebook.detail}</span>
            <span className={`rounded-full px-2 py-1 ${testResult.instagram.ok ? "bg-emerald-500/15 text-emerald-300" : "bg-destructive/15 text-destructive"}`}>Instagram : {testResult.instagram.detail}</span>
          </div>
        )}
      </div>
    </Section>
  );
}
